import { useCallback, useEffect, useState } from 'react';
import { distanceKm } from '@nu/shared';
import { api } from '../../api/client.js';

// Where everyone's van is now (GET /api/surveys/nearby: people with their
// position, and the teams), for the board's "Nearest" line and Assign.
// Looked at again every 2 minutes while the board is open (the server keeps
// Samsara's answer a minute); refresh() asks Samsara again now.
// { data, refresh, loading }
export function useNearby(enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    try { setData(await api(`/surveys/nearby${fresh ? '?fresh=1' : ''}`)); } catch { /* keep the last */ } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const t = setInterval(() => load(), 120_000);
    return () => clearInterval(t);
  }, [enabled, load]);
  return { data, loading, refresh: () => load(true) };
}

export const km = (n) => `${n < 10 ? n.toFixed(1) : Math.round(n)} km`;

// The nearest engineer and the nearest team to a job (straight line to their
// van), or null when the job or nobody has a position.
// { person: { name, km, team }, team: { name, km } | null }
export function nearestTo(job, nearby) {
  if (!nearby || job.lat == null || job.lon == null) return null;
  const dist = new Map();
  for (const p of nearby.people || []) if (p.position) dist.set(p.id, { name: p.name, km: distanceKm(job, p.position) });
  if (!dist.size) return null;
  const [personId, person] = [...dist].sort((a, b) => a[1].km - b[1].km)[0];
  let team = null;
  for (const t of nearby.teams || []) {
    const kms = t.memberIds.map((id) => dist.get(id)?.km).filter((x) => x != null);
    if (kms.length && (!team || Math.min(...kms) < team.km)) team = { name: t.name, km: Math.min(...kms) };
  }
  const own = (nearby.teams || []).find((t) => t.memberIds.includes(personId));
  return { person: { ...person, team: own?.name || null }, team };
}

// "Sullaiman Ely (Team 2) 2.5 km", plus the nearest team when it's another.
export function nearestText(n) {
  if (!n) return '';
  const who = `${n.person.name}${n.person.team ? ` (${n.person.team})` : ''} ${km(n.person.km)}`;
  return n.team && n.team.name !== n.person.team ? `${who} · nearest team ${n.team.name} ${km(n.team.km)}` : who;
}

// "HH:MM" now: when a plan sets off.
export function nowHm(d = new Date()) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// The clock time `seconds` after `start` ("HH:MM"), "+1 day" past midnight.
export function clockAfter(start, seconds) {
  const [h, m] = String(start || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const t = h * 60 + m + Math.round(seconds / 60);
  const day = Math.floor(t / 1440);
  return `${String(Math.floor((t % 1440) / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}${day ? ' (+1 day)' : ''}`;
}

// 1h 05m, 45m.
export const hm = (seconds) => {
  const m = Math.round(seconds / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};
